import { useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import Link from '@/routing/Link'
import { usePathname } from '@/routing/navigation'
import {
  ChevronLeft,
  ExternalLink,
  KeyRound,
  LayoutDashboard,
  Loader2,
  type IconComponent,
  Moon,
  MoreVertical,
  Palette,
  ScanSearch,
  Shuffle,
  Sun,
  ToggleRight,
  User,
  Users,
} from '@digital-twins/design-system/icons'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { Text } from '@/components/ui/Text'
import { usersApi } from '@/lib/api'
import { useProfile } from '@/lib/hooks'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme } from '@/lib/theme-provider'
import { useBrand } from '@/lib/brand-provider'
import { getDtVite } from '@/lib/runtime-env'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn, capitalizeFirst } from '@/lib/utils'
import { useLogoNeedsInversion } from '@/lib/hooks/use-logo-needs-inversion'
import { getUserInitials, getAvatarPaletteForSeed, getAvatarPalette, seedToPaletteIndex, AVATAR_PALETTE_COUNT } from '@/lib/utils/avatar'

import { isNavActive, SidebarTooltip } from './Sidebar'
import { SIDEBAR_ICON_SIZE } from './sidebar.constants'

/** Matches the cubic-bezier used by the Framer Motion sidebar width animation in AppShell */
const SIDEBAR_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

function mainAppBaseUrl(): string {
  const raw = getDtVite('DT_VITE_MAIN_APP_URL') || 'http://localhost:3005'
  return raw.replace(/\/$/, '')
}

type NavItemInternal = {
  kind?: 'internal'
  href: string
  label: string
  icon: IconComponent
}

type NavItemExternal = {
  kind: 'external'
  href: string
  label: string
  icon: IconComponent
}

type NavItem = NavItemInternal | NavItemExternal

interface BackofficeSidebarProps {
  isCollapsed: boolean
  toggleSidebar: () => void
}

export function BackofficeSidebar(props: BackofficeSidebarProps) {
  const { isCollapsed, toggleSidebar } = props

  const pathname = usePathname()
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const { user: authUser, logout } = useAuth()
  const { data: profile, mutate: refreshProfile } = useProfile()
  const { brandSettings, brandLogoUrlOnDark } = useBrand()
  const { resolvedTheme, theme, toggleTheme } = useTheme()
  // structural only: drives logo asset swap, brand-logo dark URL preference,
  // logo image filter, and Sun/Moon icon swap. All color choices flow through tokens.
  const isDark = resolvedTheme === 'dark'
  // Server-measured on save: a saved logo cannot be inspected in the browser,
  // because the images bucket serves it without a CORS header.
  const logoNeedsInversion = useLogoNeedsInversion(
    brandLogoUrlOnDark ? null : brandSettings.brandLogoUrl,
    brandSettings.brandLogoNeedsInversion,
  )

  const mainAppUrl = useMemo(() => mainAppBaseUrl(), [])

  const userName = profile?.name
    ? profile.name.split(' ').map((part: string) => capitalizeFirst(part)).join(' ')
    : 'User'
  const userRole = profile?.orgRole ? capitalizeFirst(profile.orgRole) : ''
  const avatarSeed = profile?.avatarSeed ?? profile?.email ?? profile?.name ?? 'user'
  const avatarPalette = useMemo(() => getAvatarPaletteForSeed(avatarSeed), [avatarSeed])
  const avatarInitials = useMemo(() => getUserInitials(profile?.name, profile?.email), [profile?.name, profile?.email])

  const [tooltipsReady, setTooltipsReady] = useState(isCollapsed)
  const tooltipsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setTooltipsReady(false)
    if (tooltipsTimerRef.current) clearTimeout(tooltipsTimerRef.current)
    tooltipsTimerRef.current = setTimeout(() => setTooltipsReady(isCollapsed), 350)
    return () => {
      if (tooltipsTimerRef.current) clearTimeout(tooltipsTimerRef.current)
    }
  }, [isCollapsed])

  const [shuffling, setShuffling] = useState(false)
  const [overridePalette, setOverridePalette] = useState<{ bg: string; fg: string } | null>(null)

  const handleShuffleAvatar = async (e: MouseEvent) => {
    e.stopPropagation()
    if (shuffling) return

    const currentIndex = seedToPaletteIndex(avatarSeed)
    const nextIndex = (currentIndex + 1) % AVATAR_PALETTE_COUNT
    const nextPalette = getAvatarPalette(nextIndex)
    const newSeed = `color-${nextIndex}-${Date.now()}`

    setShuffling(true)
    setOverridePalette(nextPalette)

    try {
      await usersApi.updateProfile({ avatarSeed: newSeed })
      await refreshProfile()
      setOverridePalette(null)
    } catch {
      setOverridePalette(null)
    } finally {
      setShuffling(false)
    }
  }

  const navItems: NavItem[] = useMemo(
    () => [
      { href: '/explore', label: 'Overview', icon: LayoutDashboard },
      { href: '/explore/flags', label: 'Flags', icon: ToggleRight },
      { href: '/explore/users', label: 'Users', icon: User },
      { href: '/explore/personas', label: 'Personas', icon: ScanSearch },
      { href: '/explore/theme-maker', label: 'Theme', icon: Palette },
      { href: '/explore/operator-password', label: 'Backoffice password', icon: KeyRound },
      { kind: 'external' as const, href: mainAppUrl, label: 'Main app', icon: ExternalLink },
    ],
    [mainAppUrl],
  )

  const internalHrefs = navItems.filter((i): i is NavItemInternal => i.kind !== 'external').map((i) => i.href)
  const hasExactMatch = internalHrefs.some((href) => pathname === href)

  const dropdownContentClass = cn(
    'w-56 bg-background',
    'border-[var(--sidebar-popover-border)]',
  )

  const dropdownItemClass = cn(
    'flex items-center gap-3 p-3 text-sm transition-colors',
    'text-sidebar-foreground/80 hover:text-sidebar-foreground focus:text-sidebar-foreground',
    'hover:bg-[var(--sidebar-popover-item-hover-bg)] focus:bg-[var(--sidebar-popover-item-hover-bg)]',
  )

  const renderAvatar = () => (
    <UserAvatar initials={avatarInitials} name={userName} size="sm" />
  )

  const handleLogout = async () => {
    await logout()
  }

  const settingsHref = `${mainAppUrl}/settings`

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <div className="flex h-full w-full flex-col bg-background px-2 py-2 text-sidebar-foreground">
        <div className={cn('pb-3', isCollapsed && 'flex justify-center')}>
          {isCollapsed ? (
            <SidebarTooltip content="Expand sidebar" enabled={tooltipsReady}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label="Expand sidebar"
                  aria-expanded={!isCollapsed}
                  className="group/logo relative flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-sidebar-foreground/8"
                >
                  <img
                    src="/sidebar-brand-logo.svg"
                    alt="Fairgen"
                    className="h-7 w-[29px] object-contain transition-opacity duration-150 group-hover/logo:opacity-0"
                  />
                  <ChevronLeft
                    className="absolute inset-0 m-auto h-[18px] w-[18px] rotate-180 text-sidebar-foreground/60 opacity-0 transition-opacity duration-150 group-hover/logo:opacity-100"
                    strokeWidth={1.5}
                  />
                </button>
              </TooltipTrigger>
            </SidebarTooltip>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2">
              <Link href="/explore" className="flex min-w-0 flex-col items-start gap-0.5">
                <img
                  src={isDark ? '/fairgen-logo-dark.svg' : '/fairgen-logo-light.svg'}
                  alt="Fairgen"
                  className="h-7 w-auto shrink-0 object-contain"
                />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                  Backoffice
                </span>
              </Link>

              <button
                type="button"
                onClick={toggleSidebar}
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
                aria-expanded={!isCollapsed}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sidebar-foreground/8"
              >
                <ChevronLeft className="h-4 w-4 text-sidebar-foreground/70" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <nav
          className={cn(
            'flex flex-1 flex-col py-2 transition-[gap,padding] duration-300',
            isCollapsed ? 'items-center gap-1.5' : 'gap-1 px-1',
          )}
          style={{ transitionTimingFunction: SIDEBAR_EASE }}
        >
          {navItems.map((item) => {
            const Icon = item.icon
            const isExternal = item.kind === 'external'

            if (isExternal) {
              return (
                <SidebarTooltip key={item.href} content={item.label} enabled={tooltipsReady}>
                  <TooltipTrigger asChild>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className={cn(
                        'flex items-center overflow-hidden rounded-[4px] transition-all duration-300',
                        isCollapsed
                          ? 'h-8 w-8 justify-center text-sidebar-foreground/72 hover:bg-sidebar-foreground/8 hover:text-sidebar-foreground'
                          : 'min-h-8 px-2 py-2 text-sidebar-foreground/82 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground',
                      )}
                      style={{ transitionTimingFunction: SIDEBAR_EASE }}
                    >
                      <Icon
                        style={{ width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }}
                        strokeWidth={1.5}
                        className="shrink-0"
                      />
                      <span
                        className={cn(
                          'overflow-hidden whitespace-nowrap text-[18px] font-medium leading-5 transition-[max-width,opacity,margin] duration-300',
                          isCollapsed
                            ? 'ml-0 max-w-0 opacity-0'
                            : 'ml-2 max-w-[180px] opacity-100',
                        )}
                        style={{ transitionTimingFunction: SIDEBAR_EASE }}
                      >
                        {item.label}
                      </span>
                    </a>
                  </TooltipTrigger>
                </SidebarTooltip>
              )
            }

            const isActive = isNavActive(item.href, pathname ?? '', hasExactMatch)

            return (
              <SidebarTooltip key={item.href} content={item.label} enabled={tooltipsReady}>
                <TooltipTrigger asChild>
                  <Link
                    href={item.href}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'flex items-center overflow-hidden rounded-[4px] transition-all duration-300',
                      isCollapsed
                        ? cn(
                            'h-8 w-8 justify-center',
                            isActive
                              ? 'text-sidebar-foreground'
                              : 'text-sidebar-foreground/72 hover:bg-sidebar-foreground/8 hover:text-sidebar-foreground',
                          )
                        : cn(
                            'min-h-8 px-2 py-2',
                            isActive
                              ? cn(
                                  // Pill bg via token: transparent in light, platform-deep in dark.
                                  // Active fg token is identical in both modes by design.
                                  'bg-[hsl(var(--backoffice-nav-active-bg))] text-[hsl(var(--sidebar-nav-active-fg))]',
                                  // structural: typographic asymmetry — light bolds the label
                                  // in lieu of a pill background; dark uses the pill instead.
                                  !isDark && 'font-semibold',
                                )
                              : 'text-sidebar-foreground/82 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground',
                          ),
                    )}
                    style={{ transitionTimingFunction: SIDEBAR_EASE }}
                  >
                    <Icon
                      style={{ width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }}
                      strokeWidth={1.5}
                      className="shrink-0"
                    />
                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap text-[18px] font-medium leading-5 transition-[max-width,opacity,margin] duration-300',
                        isCollapsed
                          ? 'ml-0 max-w-0 opacity-0'
                          : 'ml-2 max-w-[180px] opacity-100',
                      )}
                      style={{ transitionTimingFunction: SIDEBAR_EASE }}
                    >
                      {item.label}
                    </span>
                  </Link>
                </TooltipTrigger>
              </SidebarTooltip>
            )
          })}
        </nav>

        {!isCollapsed && brandSettings.brandLogoUrl && (
          <div className="px-2 pb-2 pt-3">
            <img
              src={(isDark && brandLogoUrlOnDark) ? brandLogoUrlOnDark : brandSettings.brandLogoUrl}
              alt="Your brand"
              className="h-5 w-auto max-w-[120px] object-contain opacity-75"
              style={(isDark && !brandLogoUrlOnDark && logoNeedsInversion) ? { filter: 'var(--brand-logo-filter)' } : undefined}
            />
          </div>
        )}

        <div
          className={cn(
            'relative pt-2',
            isCollapsed ? 'flex justify-center' : 'group/avatar px-1',
          )}
        >
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center transition-colors',
                  isCollapsed
                    ? 'rounded-md p-1 hover:bg-sidebar-foreground/8'
                    : 'w-full rounded-md px-2 py-2 hover:bg-sidebar-foreground/6',
                )}
                title={userName}
                aria-label={`${userName} menu`}
              >
                {renderAvatar()}
                <div
                  className={cn(
                    'overflow-hidden transition-[max-width,opacity,margin] duration-300',
                    isCollapsed
                      ? 'ml-0 max-w-0 opacity-0'
                      : 'ml-3 min-w-0 flex-1 max-w-[200px] opacity-100',
                  )}
                    style={{ transitionTimingFunction: SIDEBAR_EASE }}
                  >
                    <div className="whitespace-nowrap text-left">
                      <Text className="truncate text-[16px] font-semibold leading-none text-sidebar-foreground">{userName}</Text>
                      {userRole ? (
                        <Text className="mt-1 truncate text-[12px] leading-none text-[var(--sidebar-user-role-fg)]">
                          {userRole}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                <MoreVertical
                  className={cn(
                    'h-4 w-4 shrink-0 text-sidebar-foreground/50 overflow-hidden transition-[max-width,opacity] duration-300',
                    isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[16px] opacity-100',
                  )}
                  strokeWidth={1.5}
                  style={{ transitionTimingFunction: SIDEBAR_EASE }}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side={isCollapsed ? 'right' : 'top'}
              sideOffset={isCollapsed ? undefined : 8}
              className={dropdownContentClass}
            >
              <DropdownMenuItem asChild className={dropdownItemClass}>
                <a href={settingsHref} target="_blank" rel="noreferrer">
                  Open settings in app
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(event) => {
                  event.preventDefault()
                  toggleTheme()
                }}
                className={dropdownItemClass}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
                <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-sidebar-border/20 dark:bg-[hsl(var(--muted))]" />
              <DropdownMenuItem onClick={handleLogout} className={dropdownItemClass}>
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {!isCollapsed && authUser && (
            <button
              type="button"
              onClick={handleShuffleAvatar}
              aria-label="Shuffle avatar"
              className="absolute left-3 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity duration-150 group-hover/avatar:opacity-100"
            >
              {shuffling ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" strokeWidth={1.5} />
              ) : (
                <Shuffle className="h-4 w-4 text-white" strokeWidth={1.5} />
              )}
            </button>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
